/**
 * Create lesson workspace — inline composer for generating personal lessons.
 * It owns the same analyse -> choose -> generate flow as the modal, but keeps
 * creation visible on the learner dashboard.
 */
import { useCallback, useState, type CSSProperties } from 'react';
import { toast } from 'sonner';
import { ArrowUp, Clock, Paperclip, Sparkles, X } from 'lucide-react';
import type { LessonSuggestion } from '@formency/shared';
import { Button, FileTrigger } from '@/interfaces/learner/ui';
import {
  useAnalyzeContent,
  useGenerateFromAnalysis,
  useRefineAnalysis,
  useSubscription,
} from '@/shared/api/hooks';
import { SuggestionPhase } from './SuggestionPhase';
import { LessonLimitDisplay } from './LessonLimitDisplay';
import { ImageToggle } from './ImageToggle';

const SUGGESTIONS = [
  'Sepsis bundle protocols',
  'Atrial fibrillation rate control',
  'DKA management in adults',
  'Maple Syrup Urine Disease',
];

const ACCEPTED_FILE_TYPES = ['.pdf', '.docx', '.txt'];

type CreateBoxVariant = 'default' | 'quiet';

interface CreateBoxProps {
  variant?: CreateBoxVariant;
}

const workspaceStyle: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 'var(--dashboard-canvas-radius)',
  border: '1px solid color-mix(in srgb, var(--color-accent) 18%, var(--color-surface-border))',
  background: 'var(--dashboard-canvas-bg)',
  boxShadow: 'var(--dashboard-canvas-shadow)',
};

const quietWorkspaceStyle: CSSProperties = {
  border: '1px solid var(--dashboard-divider)',
  background: 'color-mix(in srgb, var(--color-surface-solid) 74%, transparent)',
  boxShadow: 'var(--dashboard-panel-shadow)',
};

const labelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  margin: 0,
  color: 'var(--color-accent-text)',
};

const quietLabelStyle: CSSProperties = {
  ...labelStyle,
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--type-meta-size)',
  fontWeight: 'var(--weight-semibold)',
  letterSpacing: 'var(--tracking-normal)',
};

const promptStyle: CSSProperties = {
  width: '100%',
  minHeight: 58,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  resize: 'vertical',
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-2xl)',
  fontWeight: 'var(--weight-medium)',
  color: 'var(--color-text)',
  lineHeight: 'var(--leading-tight)',
  letterSpacing: 'var(--tracking-normal)',
  padding: 0,
  display: 'block',
};

const quietPromptStyle: CSSProperties = {
  minHeight: 46,
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xl)',
  fontWeight: 'var(--weight-semibold)',
};

const metaStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--type-meta-size)',
  fontWeight: 'var(--weight-medium)',
  color: 'var(--color-text-muted)',
  margin: 0,
};

const fileChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '5px 10px',
  borderRadius: 'var(--radius-full)',
  background: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-text)',
  fontSize: 'var(--type-meta-size)',
  fontWeight: 'var(--weight-medium)',
  fontFamily: 'var(--font-sans)',
  alignSelf: 'flex-start',
  maxWidth: '100%',
};

export default function CreateBox({ variant = 'default' }: CreateBoxProps) {
  const isQuiet = variant === 'quiet';
  const [prompt, setPrompt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<LessonSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [copyrightConfirmed, setCopyrightConfirmed] = useState(false);
  const [refineFeedback, setRefineFeedback] = useState('');
  const [imagesEnabled, setImagesEnabled] = useState(true);

  const { data: subscription } = useSubscription();
  const analyzeMutation = useAnalyzeContent();
  const refineMutation = useRefineAnalysis();
  const generateMutation = useGenerateFromAnalysis();

  const trimmedPrompt = prompt.trim();
  const hasInput = trimmedPrompt.length >= 3 || file !== null;
  const isFileFlow = file !== null;
  const isChoosing = suggestions.length > 0;
  const isAnalyzing = analyzeMutation.isPending || refineMutation.isPending;

  // Subscription-derived gating flags. When subscription is undefined (loading),
  // assume the user has access — the server enforces the truth. The button still
  // disables on `!hasInput || isAnalyzing` to prevent empty submits.
  const hasSubscription = (subscription?.entitlements.lessonsPerMonth ?? 1) > 0;
  const hasQuota = (subscription?.usage.lessonsRemaining ?? 1) > 0;
  const hasInFlight = subscription?.usage.hasInFlightGeneration ?? false;
  const blocked = !hasSubscription || !hasQuota || hasInFlight;

  const canGenerate = selectedIndex !== null && (!isFileFlow || copyrightConfirmed) && !blocked;

  const resetSuggestions = useCallback(() => {
    setAnalysisId(null);
    setSuggestions([]);
    setSelectedIndex(null);
    setCopyrightConfirmed(false);
    setRefineFeedback('');
    analyzeMutation.reset();
    refineMutation.reset();
  }, [analyzeMutation, refineMutation]);

  const handleAnalyze = useCallback(() => {
    if (!hasInput) return;

    analyzeMutation.mutate(
      {
        prompt: trimmedPrompt || undefined,
        file: file ?? undefined,
      },
      {
        onSuccess: (result) => {
          setAnalysisId(result.analysisId);
          setSuggestions(result.suggestions);
          setSelectedIndex(
            result.suggestions.length === 1 ? (result.suggestions[0]?.index ?? 0) : null
          );
          setCopyrightConfirmed(false);
          setRefineFeedback('');
        },
        onError: (error) => {
          toast.error(
            error instanceof Error ? error.message : 'Analysis failed. Please try again.'
          );
        },
      }
    );
  }, [analyzeMutation, file, hasInput, trimmedPrompt]);

  const handleRefine = useCallback(
    (feedback: string) => {
      if (!analysisId) return;

      refineMutation.mutate(
        { analysisId, feedback },
        {
          onSuccess: (result) => {
            setSuggestions(result.suggestions);
            setSelectedIndex(
              result.suggestions.length === 1 ? (result.suggestions[0]?.index ?? 0) : null
            );
            setCopyrightConfirmed(false);
            setRefineFeedback('');
          },
          onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Refinement failed.');
          },
        }
      );
    },
    [analysisId, refineMutation]
  );

  const handleGenerate = useCallback(() => {
    if (!analysisId || selectedIndex === null) return;

    generateMutation.mutate(
      { analysisId, suggestionIndex: selectedIndex, imagesEnabled },
      {
        onSuccess: () => {
          toast.success('Lesson generation started');
          setPrompt('');
          setFile(null);
          setImagesEnabled(true);
          resetSuggestions();
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Generation failed.');
        },
      }
    );
  }, [analysisId, generateMutation, imagesEnabled, resetSuggestions, selectedIndex]);

  function applyChip(topic: string) {
    setPrompt(topic);
    resetSuggestions();
  }

  function handlePromptChange(value: string) {
    setPrompt(value);
    if (isChoosing) resetSuggestions();
  }

  return (
    <>
      <section
        className={isQuiet ? 'create-workspace create-workspace--quiet' : 'create-workspace'}
        style={isQuiet ? { ...workspaceStyle, ...quietWorkspaceStyle } : workspaceStyle}
      >
        <div className="create-workspace-inner">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 14,
            }}
          >
            <p
              className={isQuiet ? undefined : 'text-eyebrow'}
              style={isQuiet ? quietLabelStyle : labelStyle}
            >
              {!isQuiet && <Sparkles size={15} strokeWidth={2.2} />}
              Create lesson
            </p>
            {subscription && <LessonLimitDisplay remaining={subscription.usage.lessonsRemaining} />}
          </div>

          {!isChoosing ? (
            <>
              <textarea
                className="create-workspace-prompt"
                value={prompt}
                onChange={(event) => handlePromptChange(event.target.value)}
                placeholder="What would you like to learn next?"
                rows={2}
                maxLength={1000}
                style={isQuiet ? { ...promptStyle, ...quietPromptStyle } : promptStyle}
              />

              {file && (
                <div style={fileChipStyle}>
                  <Paperclip size={14} strokeWidth={2} />
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </span>
                  <button
                    type="button"
                    aria-label="Remove file"
                    onClick={() => setFile(null)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </div>
              )}

              <div className="create-idea-row" aria-label="Suggested lesson ideas">
                {SUGGESTIONS.map((topic) => (
                  <button
                    key={topic}
                    type="button"
                    className="create-idea-pill"
                    onClick={() => applyChip(topic)}
                  >
                    {topic}
                  </button>
                ))}
              </div>

              <div
                style={{
                  height: 1,
                  background: isQuiet ? 'var(--dashboard-divider)' : 'var(--color-surface-border)',
                  margin: isQuiet ? '14px 0 12px' : '16px 0 14px',
                }}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <FileTrigger
                  acceptedFileTypes={ACCEPTED_FILE_TYPES}
                  onSelect={(files) => setFile(files?.[0] ?? null)}
                >
                  <Button
                    type="button"
                    aria-label="Attach a document"
                    variant="secondary"
                    size="sm"
                    icon={<Paperclip size={16} strokeWidth={2} />}
                  >
                    Attach
                  </Button>
                </FileTrigger>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    ...metaStyle,
                  }}
                >
                  <Clock size={14} strokeWidth={2} />
                  ~10 min lesson
                </span>

                {subscription && (
                  <ImageToggle
                    enabled={imagesEnabled && subscription.entitlements.imageSearch}
                    onChange={setImagesEnabled}
                    disabled={!subscription.entitlements.imageSearch}
                    upgradeHint="Upgrade to Pro or Max to include images."
                  />
                )}

                <div style={{ marginLeft: 'auto' }}>
                  <Button
                    variant={isQuiet ? 'primary' : 'gradient'}
                    size="md"
                    icon={<ArrowUp size={17} strokeWidth={2.5} />}
                    onPress={handleAnalyze}
                    isDisabled={!hasInput || isAnalyzing || blocked}
                    loading={isAnalyzing}
                  >
                    {isAnalyzing ? 'Preparing...' : isQuiet ? 'Create lesson' : 'Generate'}
                  </Button>
                </div>
              </div>

              {!hasSubscription && (
                <p style={{ marginTop: 10, ...metaStyle }}>
                  An active plan is required to generate lessons.
                </p>
              )}
              {hasSubscription && !hasQuota && (
                <p style={{ marginTop: 10, ...metaStyle }}>
                  Monthly lesson limit reached. Resets on{' '}
                  {subscription?.usage.periodEnd
                    ? new Date(subscription.usage.periodEnd).toLocaleDateString()
                    : 'your next renewal'}
                  .
                </p>
              )}
              {hasInFlight && (
                <p style={{ marginTop: 10, ...metaStyle }}>
                  Another lesson is generating. Wait for it to finish.
                </p>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <SuggestionPhase
                suggestions={suggestions}
                selectedIndex={selectedIndex}
                onSelect={setSelectedIndex}
                isFileFlow={isFileFlow}
                copyrightConfirmed={copyrightConfirmed}
                onCopyrightChange={setCopyrightConfirmed}
                refineFeedback={refineFeedback}
                onRefineFeedbackChange={setRefineFeedback}
                onRefine={handleRefine}
                isRefining={refineMutation.isPending}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  borderTop: '1px solid var(--color-surface-border)',
                  paddingTop: 16,
                }}
              >
                <Button variant="secondary" size="md" onPress={resetSuggestions}>
                  Edit prompt
                </Button>
                <Button
                  variant={isQuiet ? 'primary' : 'gradient'}
                  size="md"
                  icon={<ArrowUp size={17} strokeWidth={2.5} />}
                  onPress={handleGenerate}
                  isDisabled={!canGenerate || generateMutation.isPending}
                  loading={generateMutation.isPending}
                >
                  {generateMutation.isPending ? 'Starting...' : 'Create lesson'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .create-workspace-inner {
          position: relative;
          z-index: 1;
          padding: var(--dashboard-canvas-padding);
        }

        .create-workspace-prompt::placeholder {
          color: color-mix(in srgb, var(--color-text-muted) 88%, var(--color-text));
        }

        .create-idea-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 6px;
        }

        .create-idea-pill {
          min-height: 28px;
          max-width: 100%;
          padding: 3px 10px;
          border-radius: var(--radius-full);
          border: 1px solid color-mix(in srgb, var(--color-border) 74%, transparent);
          background: color-mix(in srgb, var(--color-surface-solid) 58%, transparent);
          color: var(--color-text-secondary);
          font-family: var(--font-sans);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-medium);
          line-height: 1.3;
          white-space: nowrap;
          cursor: pointer;
          transition:
            border-color var(--motion-fast) var(--motion-ease-out),
            background var(--motion-fast) var(--motion-ease-out),
            color var(--motion-fast) var(--motion-ease-out);
        }

        .create-idea-pill:hover {
          border-color: var(--color-accent-outline);
          background: var(--color-accent-subtle);
          color: var(--color-accent-text);
        }

        .create-workspace--quiet .create-idea-pill {
          background: color-mix(in srgb, var(--color-surface-solid) 72%, transparent);
        }

        @media (max-width: 720px) {
          .create-workspace-inner {
            padding: var(--dashboard-canvas-padding);
          }

          .create-workspace-prompt {
            font-size: var(--text-xl) !important;
            min-height: 62px !important;
          }
        }
      `}</style>
    </>
  );
}
